// ─────────────────────────────────────────────────────────────────────────────
// localStorage that cannot throw.
//
// In Instagram's and Facebook's in-app browsers — and in Safari private mode,
// and in any Android WebView with DOM storage switched off — `localStorage` can
// throw a SecurityError on *property access*, not merely on get/set. An
// unguarded read therefore takes down whatever is running at the time. When
// that read happens during a render (or at module scope) React never mounts,
// `#root` stays empty, and the visitor sits looking at a permanently white
// page with nothing to click. That is exactly what ad traffic from Instagram
// was hitting.
//
// Everything here degrades to an in-memory Map: values then survive the tab but
// not the session, which is the right trade for a browser that has refused to
// persist anything. Nothing in this module can throw.
// ─────────────────────────────────────────────────────────────────────────────

const memory = new Map();

// `undefined` = not yet probed, `null` = probed and unusable.
let backing;

function resolve() {
  if (backing !== undefined) return backing;

  backing = null;
  try {
    if (typeof window === 'undefined') return backing;
    const ls = window.localStorage;
    // Availability is not enough — Safari private mode hands back a store whose
    // every write throws a quota error, so probe with a real round trip.
    const probe = '__bl_probe__';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    backing = ls;
  } catch {
    backing = null;
  }
  return backing;
}

export function getItem(key) {
  const ls = resolve();
  if (ls) {
    try {
      return ls.getItem(key);
    } catch {
      /* fall through to memory */
    }
  }
  return memory.has(key) ? memory.get(key) : null;
}

export function setItem(key, value) {
  const v = String(value);
  memory.set(key, v);
  const ls = resolve();
  if (ls) {
    try {
      ls.setItem(key, v);
    } catch {
      /* memory already holds it */
    }
  }
}

export function removeItem(key) {
  memory.delete(key);
  const ls = resolve();
  if (ls) {
    try {
      ls.removeItem(key);
    } catch {
      /* nothing else to do */
    }
  }
}

/** True when the browser actually persists. Handy for diagnostics. */
export function isPersistent() {
  return resolve() !== null;
}

export const safeStorage = { getItem, setItem, removeItem, isPersistent };

export default safeStorage;
